import AuthGuard from '@components/AuthGuard';
import BottomSheetHeader from '@components/BottomSheetHeader';
import Column from '@components/Column';
import Image from '@components/Image';
import ImageIndicator from '@components/ImageIndicator';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import useCopyShopAddress from '@hooks/shop/useCopyShopAddress';
import useShop from '@hooks/shop/useShop';
import useAppCustom from '@hooks/useAppCustom';
import Copy from '@icons/Copy';
import FavoriteFill from '@icons/favorite/FavoriteFill';
import FavoriteLine from '@icons/favorite/FavoriteLine';
import PhoneFill from '@icons/phone/PhoneFill';
import { ShopOrderStatus } from '@models/shop/common';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { Fragment, useRef, useState } from 'react';
import Slider from 'react-slick';

import useToggleMyShop from './hooks/useToggleMyShop';
import OrderButton from './OrderButton';
import styles from './styles.module.scss';

interface Params {
    shopId: string;
}

const ShopDetailBottomSheet:ActivityComponentType<Params> = ({ params }: { params: Params }) => {
  const { shopId } = params;
  const shop = useShop(shopId);
  const [index, setIndex] = useState<number>(0);
  const sliderRef = useRef<Slider>(null);
  const { toggleMyShop } = useToggleMyShop(shop);
  const copyAddress = useCopyShopAddress(shop?.address);
  const { useAddress, usePhoneNumber, useHashtag } = useAppCustom().shop;
  const imageExists = (shop?.images.length || 0) !== 0;
  return (
        <BottomSheet>
            <Container>
                <BottomSheetHeader>
                    매장 정보
                </BottomSheetHeader>
                <Column style={{ flex: 1, overflow: 'hidden' }}>
                    {imageExists && (
                        <Fragment>
                            {(shop?.images.length || 0) > 1 ? (
                                <Slider dots={false}
                                        ref={sliderRef}
                                        afterChange={setIndex}
                                        infinite>
                                    {shop?.images.map((img, j) => (
                                        <Image src={img}
                                               key={j.toString()}
                                               width={720}
                                               height={480}
                                               alt={`매장 이미지 ${j}`}
                                               className={styles.thumbnail}/>
                                    ))}
                                </Slider>
                            ) : (
                                <Image src={shop?.images[0]!}
                                       width={720}
                                       height={480}
                                       className={styles.thumbnail}
                                       alt='매장 이미지' />
                            )}
                        </Fragment>
                    )}
                    <Contents>
                        {imageExists && (
                            <ImageIndicator size={shop?.images.length}
                                            current={index}
                                            slickNext={() => sliderRef.current?.slickNext()}
                                            slickPrev={() => sliderRef.current?.slickPrev()} />
                        )}
                        <Column className={styles.contents}
                                fullWidth
                                spacing={16}>
                            <Row justify='space-between'
                                 fullWidth>
                                <Text size='3xl' className={styles.title}
                                      fontWeight='bold'>
                                    {shop?.name}
                                </Text>
                                <AuthGuard className={styles.favorite}
                                           flows={[
                                             { activityName: 'ShopListActivity', activityParams: { type: 'LIST' } },
                                             { activityName: 'ShopDetailBottomSheet', activityParams: { shopId } },
                                           ]}>
                                    <FavoriteFill size={28}
                                                  onClick={toggleMyShop}
                                                  color={shop?.isMyShop ? colors.red : colors.gray4} />
                                </AuthGuard>
                            </Row>
                            {useAddress && (
                                <Text size='lg'>
                                    {shop?.address}
                                </Text>
                            )}
                            <Row spacing={14}>
                                {useAddress && (
                                    <Row spacing={2} onClick={copyAddress}>
                                        <Copy color={colors.gray7}/>
                                        <Text color={colors.gray7}>주소 복사</Text>
                                    </Row>
                                )}
                                {usePhoneNumber && (
                                    <a href={`tel:${shop?.phoneNumber}`}>
                                        <Row>
                                            <PhoneFill color={colors.gray7} size={16}/>
                                            <Text color={colors.gray7}>전화문의</Text>
                                        </Row>
                                    </a>
                                )}
                            </Row>
                            {useHashtag && (
                                <HashTags>
                                    {shop?.hashtag.map((tag, index) => (
                                        <Text key={`${tag} ${index}`} color={colors.gray6}>
                                            #{tag}
                                        </Text>
                                    ))}
                                </HashTags>
                            )}
                            <div className={styles.divider}/>
                            {(shop?.phoneNumber && usePhoneNumber) && (
                                <Row spacing={8}>
                                    <Text color={colors.primary} fontWeight='bold'>매장전화</Text>
                                    <Text >{shop?.phoneNumber}</Text>
                                </Row>
                            )}
                            {shop?.openhours && (
                                <Row spacing={8} align='flex-start'>
                                    <Text color={colors.primary} fontWeight='bold'>영업시간</Text>
                                    <Text className={styles.openHours}>
                                        {shop?.openhours}
                                    </Text>
                                </Row>
                            )}
                        </Column>
                        <OrderButton shopId={shopId}
                                     shopStatus={shop?.shopStatus || 'PENDING'}
                                     status={shop?.orderStatus || 'CLOSED'} />
                    </Contents>
                </Column>
            </Container>
        </BottomSheet>
  );
};

const Container = styled.div`
    max-height: 100vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const Contents = styled.div`
    flex: 1;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const HashTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
`;

export default ShopDetailBottomSheet;
